import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Table, Spin } from "antd";
import type { TableProps } from "antd";
import { v4 as uuidv4 } from "uuid";
import CustomSearchInput from "sipli-fleet-core/components/CustomSharedSearchInput/CustomSharedSearchInput";
import CustomPagination from "sipli-fleet-core/components/CustomSharedPagination/CustomSharedPagination";
import CustomSortingDropdown from "sipli-fleet-core/components/CustomSharedSortingDropdown/CustomSharedSortingDropdown";
import CustomDownload from "sipli-fleet-core/components/CustomSharedTableFilters/Download";
import CustomFiltration, { FilterConfig as FiltrationFilterConfig } from "sipli-fleet-core/components/CustomSharedTableFilters/Filtration";
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE,
} from "src/util/util.constant";
import { cn } from "src/util/util.service";
import { useAppContext } from "src/contextApi/AppContext/AppContext";

import "./CustomTable.css";

export interface FilterConfig {
  FILTERS?: Record<string, FiltrationFilterConfig> & { customFilterButtons?: React.ReactNode };
}

export interface DownloadOptions {
  allStartDate?: string;
  [key: string]: unknown;
}

export interface CustomTableProps<T extends object> extends Omit<
  TableProps<T>,
  "columns" | "dataSource"
> {
  data?: T[];
  ddd?: boolean;
  columns?: TableProps<T>["columns"];

  pageSize?: number;
  currentPage?: number;
  totalCount?: number;

  showSorting?: boolean;
  headingOne?: React.ReactNode;

  filters?: FilterConfig;
  customizeColumns?: unknown[];
  selectedFilters?: Record<string, unknown>;

  handlePageSizeChange?: (pageSize: number | string) => void;
  handlePagination?: (page: number) => void;
  handleSearch?: (value: string) => void;
  handleColumnsChange?: (...args: unknown[]) => void;
  updateColumns?: (...args: unknown[]) => void;
  resetColumns?: (...args: unknown[]) => void;

  handleFiltration?: (...args: unknown[]) => void;
  handleApplyFilters?: (...args: unknown[]) => void;
  clearFilters?: () => void;

  handleSort?: (...args: unknown[]) => void;

  showSearchInput?: boolean;
  showCustomizeColumns?: boolean;
  showDownload?: boolean;
  isShowPagination?: boolean;

  downloadExportCallback?: (...args: unknown[]) => void;

  filtrationChildren?: React.ReactNode;

  tableClassName?: string;

  downloadOptions?: DownloadOptions;

  searchInput?: string;
  endExtraChildren?: React.ReactNode;
  disableFiltersWrapperFlex?: boolean;

  clientPagination?: boolean;

  rowKey?: TableProps<T>["rowKey"];

  sorting?: unknown;

  filename?: string;

  searchPlaceHolder?: string;

  tableContainerClassName?: string;
  tableFilterContainerClassName?: string;
  searchInputclassName?: string;

  filterPrefix?: React.ReactNode;

  searchInputExtraProps?: Record<string, unknown>;

  isLoading?: boolean;

  customClass?: string;

  isCustomRowClick?: boolean;
  customRowClick?: (record: T) => void;

  filterContainerClassname?: string;

  searchBtnText?: string;

  showTopHorizontalScrollbar?: boolean;
}

function CustomTable<T extends object>({
  data = [],
  //ddd = false,
  columns = [],
  pageSize = DEFAULT_PAGE_SIZE,
  currentPage = DEFAULT_CURRENT_PAGE,
  totalCount = 0,

  showSorting = false,
  headingOne = "",

  filters = {},

  //customizeColumns = [],
  selectedFilters = {},

  handlePageSizeChange = () => {},
  handlePagination = () => {},
  handleSearch = () => {},
  //handleColumnsChange = () => {},
  //updateColumns = () => {},
  //resetColumns = () => {},

  handleFiltration = () => {},
  handleApplyFilters = () => {},
  clearFilters = () => {},

  handleSort = () => {},

  showSearchInput = true,
  //showCustomizeColumns = true,
  showDownload = false,
  isShowPagination = true,

  downloadExportCallback = () => {},

  filtrationChildren = <Fragment />,

  tableClassName = "",

  downloadOptions = {},

  searchInput,

  endExtraChildren,
  disableFiltersWrapperFlex = false,

  clientPagination = false,

  rowKey,

  sorting,

  filename,

  searchPlaceHolder = "Search...",

  tableContainerClassName = "",
  tableFilterContainerClassName = "",
  searchInputclassName = "",

  filterPrefix,

  searchInputExtraProps = {},

  isLoading = false,

  customClass,

  isCustomRowClick = false,
  customRowClick,

  filterContainerClassname,

  searchBtnText,

  showTopHorizontalScrollbar = false,

  ...restProps
}: CustomTableProps<T>) {
  const { sidebarCollapsed: collapsed, isHovered } = useAppContext();
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);

  const [topScrollbarWidth, setTopScrollbarWidth] = useState(0);

  const externalOnRow = restProps.onRow;
  const externalRowClassName = restProps.rowClassName;

  const resolvedOnRow = useMemo(() => {
    if (isCustomRowClick && typeof customRowClick === "function") {
      return (record: T) => ({
        onClick: () => customRowClick(record),
      });
    }

    return externalOnRow;
  }, [customRowClick, externalOnRow, isCustomRowClick]);

  const resolvedRowClassName = useMemo(
    () => (record: T, index: number, indent: number) => {
      const baseClassName =
        typeof externalRowClassName === "function"
          ? externalRowClassName(record, index, indent)
          : (externalRowClassName ?? "");

      const rowProps =
        typeof resolvedOnRow === "function" ? resolvedOnRow(record, index) : {};

      const isClickable = typeof rowProps?.onClick === "function";

      return cn(baseClassName, {
        custom_table_row_clickable: isClickable,
      });
    },
    [externalRowClassName, resolvedOnRow],
  );

  const hasTopControls = Boolean(
    showSorting ||
    filters?.FILTERS ||
    endExtraChildren ||
    showDownload ||
    showSearchInput ||
    filterPrefix,
  );

  useEffect(() => {
    if (!showTopHorizontalScrollbar) return undefined;

    const tableContainer = tableScrollContainerRef.current;
    const topScrollbar = topScrollbarRef.current;

    if (!tableContainer || !topScrollbar) return undefined;

    const getHorizontalScrollElement = () => {
      const candidates = tableContainer.querySelectorAll<HTMLElement>(
        ".ant-table-body, .ant-table-content",
      );

      for (const node of candidates) {
        if (node.scrollWidth > node.clientWidth) {
          return node;
        }
      }
      return candidates[0] ?? null;
    };

    const tableScrollable = getHorizontalScrollElement();
    if (!tableScrollable) return undefined;

    const topInner = topScrollbar.firstElementChild;
    if (!topInner) return undefined;

    const syncWidths = () => {
      setTopScrollbarWidth(tableScrollable.scrollWidth);
      topScrollbar.scrollLeft = tableScrollable.scrollLeft;
    };

    let isSyncingFromTable = false;
    let isSyncingFromTop = false;

    const handleTableScroll = () => {
      if (isSyncingFromTop) return;
      isSyncingFromTable = true;
      topScrollbar.scrollLeft = tableScrollable.scrollLeft;
      isSyncingFromTable = false;
    };

    const handleTopScroll = () => {
      if (isSyncingFromTable) return;
      isSyncingFromTop = true;
      tableScrollable.scrollLeft = topScrollbar.scrollLeft;
      isSyncingFromTop = false;
    };

    syncWidths();

    tableScrollable.addEventListener("scroll", handleTableScroll);
    topScrollbar.addEventListener("scroll", handleTopScroll);
    window.addEventListener("resize", syncWidths);

    return () => {
      tableScrollable.removeEventListener("scroll", handleTableScroll);
      topScrollbar.removeEventListener("scroll", handleTopScroll);
      window.removeEventListener("resize", syncWidths);
    };
  }, [showTopHorizontalScrollbar, columns, data, collapsed, isHovered]);

  return (
    <div className={`overflow-hidden custom_table_container ${customClass}`}>
      <Fragment>
        {headingOne && headingOne}
        <div
          className={cn(
            "flex flex-col md:flex-row md:justify-between items-center",
            tableContainerClassName,
          )}
          style={{ padding: hasTopControls ? undefined : 0 }}
        >
          {filtrationChildren}
          <div className={cn("grow", tableFilterContainerClassName)}>
            {filterPrefix}
            {showSorting && (
              <div className="mb-2 rounded-lg md:mb-0">
                <CustomSortingDropdown
                  handleSort={handleSort}
                  sorting={sorting}
                />
              </div>
            )}
            {(filters["FILTERS"] || endExtraChildren) && (
              <div
                className={cn(
                  "items-end justify-between gap-3 mb-2 md:mb-0",
                  {
                    flex: !disableFiltersWrapperFlex,
                  },
                )}
              >
                <div className="flex-1 min-w-0">
                  {filters["FILTERS"] && (
                    <CustomFiltration
                      filters={filters["FILTERS"]}
                      handleFiltration={handleFiltration}
                      handleApplyFilters={handleApplyFilters}
                      clearFilters={clearFilters}
                      selectedFilters={selectedFilters}
                      allStartDate={downloadOptions.allStartDate}
                      filterContainerClassname={filterContainerClassname}
                      searchBtnText={searchBtnText}
                    />
                  )}
                </div>
                {endExtraChildren && (
                  <div className="shrink-0">{endExtraChildren}</div>
                )}
              </div>
            )}
            {showDownload && (
              <div className="mb-2 md:mb-0">
                <CustomDownload
                  filename={filename}
                  downloadExportCallback={downloadExportCallback}
                  handlePageSizeChange={handlePageSizeChange}
                  totalCount={totalCount}
                  downloadOptions={downloadOptions}
                />
              </div>
            )}
            {showSearchInput && (
              <CustomSearchInput
                placeholder={searchPlaceHolder}
                value={searchInput}
                handleSearch={handleSearch}
                className={searchInputclassName}
                {...searchInputExtraProps}
              />
            )}
          </div>
        </div>

        <Spin spinning={isLoading}>
          {showTopHorizontalScrollbar && (
            <div ref={topScrollbarRef} className="custom_table_top_scrollbar">
              <div style={{ width: `${topScrollbarWidth}px` }} />
            </div>
          )}

          <div
            ref={tableScrollContainerRef}
            className={cn("w-full overflow-x-auto", {
              "max-w-[100%]": !isHovered && collapsed,
            })}
          >
            <Table<T>
              className={`rounded-2xl ${tableClassName}`}
              dataSource={data ?? []}
              columns={columns}
              pagination={clientPagination ? undefined : false}
              rowHoverable={false}
              rowKey={rowKey ?? (() => uuidv4())}
              {...restProps}
              rowClassName={resolvedRowClassName}
              onRow={(record, index) => {
                const rowProps =
                  typeof resolvedOnRow === "function"
                    ? (resolvedOnRow(record, index) ?? {})
                    : {};

                if (typeof rowProps.onClick !== "function") return rowProps;

                const htmlProps = rowProps as React.HTMLAttributes<HTMLElement>;
                return {
                  ...rowProps,
                  style: {
                    ...htmlProps.style,
                    cursor: htmlProps.style?.cursor ?? "pointer",
                  },
                };
              }}
            />
          </div>
          {isShowPagination && (
            <CustomPagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={handlePagination}
              setPageSize={handlePageSizeChange}
            />
          )}
        </Spin>
      </Fragment>
    </div>
  );
}

export default CustomTable;
